import { useEffect, useState } from 'react'
import { ICPlatformVersion } from '../../../../../../utils/ic-platform/versioning'
import { usePartnerContext } from '../../../../../../utils/contexts/partner/PartnerContext.hooks'
import { PartnerType } from '../../../../../../exports/utils'
import clientEnv from '../../../../../../utils/global/clientEnv'
import { useRetailerId, usePartnerId } from '../../../../../../utils/routing/params.hooks'
import usePartnerAccessControl from '../../../../../../legacy/components/AccessControl/usePartnerAccessControl'
import {
  detectUserRegion,
  getLocalizedLegalLinks,
} from '../../../../../../components/privacy-compliance'

export type HelpMenuLink = {
  href?: string
  messageId: string
  onClick?: () => void
  openInNewTab?: boolean
}

export const useHelpMenuLinks = () => {
  const partnerContext = usePartnerContext()
  const partnerId = usePartnerId({ throwOnMissing: false })
  const retailerId = useRetailerId({ throwOnMissing: false })
  const hasAccess = usePartnerAccessControl()

  // Initialize with default English links
  const [legalLinks, setLegalLinks] = useState(() => getLocalizedLegalLinks(null))

  // Detect user region and update legal links accordingly
  useEffect(() => {
    const updateLegalLinks = async () => {
      const region = await detectUserRegion()
      const localizedLinks = getLocalizedLegalLinks(region.countryCode)
      setLegalLinks(localizedLinks)
    }

    void updateLegalLinks()
  }, [])

  const docsUrl =
    partnerContext.partnerType === PartnerType.IDP
      ? 'https://docs.instacart.com/developer_platform_api/'
      : clientEnv.PUBLIC_CLIENT_PARTNER_DOCS_URL

  const termsUrl =
    partnerContext.partnerType === PartnerType.IDP
      ? 'https://docs.instacart.com/developer_platform_api/guide/terms_and_policies/developer_terms/'
      : legalLinks.termsUrl

  const links: HelpMenuLink[] = [
    { href: docsUrl, messageId: 'navV2.userSettings.documentation' },
    { href: 'https://instacart.com/press', messageId: 'press' },
    { href: legalLinks.privacyUrl, messageId: 'privacy' },
    { href: 'https://instacart.safebase.us/', messageId: 'security_and_trust' },
    { href: termsUrl, messageId: 'terms' },
    {
      href: 'https://instacart.atlassian.net/servicedesk/customer/portal/1',
      messageId: 'service_desk',
    },
  ]

  // Add partner status link at the top if we have both partnerId and retailerId and user has access
  // Make sure this permission check is the same as the one in the external-health-dashboard domain
  const externalHealthDashboardAccessControl = {
    warehouseFeatureToggles: ['rt_ipp_external_health_dashboard'],
    permissions: ['external_health_dashboard.view'],
    minICPlatformVersion: ICPlatformVersion.r2026_01_0,
  }

  if (hasAccess(externalHealthDashboardAccessControl as any)) {
    links.unshift({
      href: `/partners/${partnerId}/warehouses/${retailerId || 0}/partner-status`,
      messageId: 'partner_status',
      openInNewTab: false,
    })
  }

  return links
}
